// Uplink bitrate adaptation.
//
// Two inputs, in priority order:
//  1. RIFT's own estimate — `recommendedBitrateBps()` and
//     `CongestionDetected.availableRateBps`, the demo's mechanism. The
//     browser driver (066ca29 through 26ba8da) never feeds the core a CongestionUpdate
//     (WebTransport exposes no QUIC cwnd), so in practice both read 0 today;
//     they are honoured whenever they report a rate.
//  2. RTT inflation from the session's RttSample probes (every 100 ms).
//     Queueing in front of a congested link shows up as RTT growth well
//     before loss, so a sustained rise over the session's baseline backs the
//     encoder off; a quiet stretch probes back up.
export const DEFAULT_RATE_CONTROL = {
    rttInflationFactor: 2,
    rttSlackMs: 60,
    decrease: 0.8,
    increase: 1.1,
    increaseAfterMs: 8_000,
    headroom: 0.85,
};
export class RateController {
    o;
    target;
    baselineRttMs = Infinity;
    smoothedRttMs = null;
    lastCongestionAt = -Infinity;
    lastChangeAt = 0;
    constructor(opts) {
        this.o = { ...DEFAULT_RATE_CONTROL, ...opts };
        this.target = this.o.maxBps;
    }
    get targetBps() {
        return this.target;
    }
    get rttMs() {
        return this.smoothedRttMs;
    }
    /** True if congestion was signalled within the last `windowMs`. */
    congested(now, windowMs = 2_000) {
        return now - this.lastCongestionAt < windowMs;
    }
    onRtt(rttMs) {
        this.smoothedRttMs = this.smoothedRttMs === null ? rttMs : this.smoothedRttMs * 0.875 + rttMs * 0.125;
        this.baselineRttMs = Math.min(this.baselineRttMs, rttMs);
    }
    /** A reported available rate (recommendedBitrateBps / CongestionDetected). */
    onAvailableRate(bps, now) {
        if (!(bps > 0))
            return;
        const cap = bps * this.o.headroom;
        if (cap < this.target * 0.95) {
            this.lastCongestionAt = now;
            this.set(cap, now);
        }
    }
    /** Called at ~1 Hz. Returns the target to apply. */
    tick(now) {
        const rtt = this.smoothedRttMs;
        if (rtt !== null && Number.isFinite(this.baselineRttMs)) {
            const threshold = this.baselineRttMs * this.o.rttInflationFactor + this.o.rttSlackMs;
            if (rtt > threshold) {
                this.lastCongestionAt = now;
                // At most one decrease per second of ticks.
                if (now - this.lastChangeAt >= 1_000)
                    this.set(this.target * this.o.decrease, now);
                return this.target;
            }
        }
        if (now - this.lastCongestionAt >= this.o.increaseAfterMs && now - this.lastChangeAt >= this.o.increaseAfterMs) {
            if (this.target < this.o.maxBps)
                this.set(this.target * this.o.increase, now);
        }
        return this.target;
    }
    /** A new session: RTT baseline is per path, relearn it. */
    resetPath() {
        this.baselineRttMs = Infinity;
        this.smoothedRttMs = null;
    }
    set(bps, now) {
        this.target = Math.round(Math.max(this.o.minBps, Math.min(this.o.maxBps, bps)));
        this.lastChangeAt = now;
    }
}
//# sourceMappingURL=rate-control.js.map